import { type VFC } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { toSafeInt } from "widgets/utils";

import { useURLViewID } from "../../app-utils/hooks/useUrl.js";
import { type InputTableQuerySpec, type VariantAnalysisGroup } from "../../graphql.js";
import { type IMutationState } from "../../redux/modules/mutation.js";
import { type IAppState } from "../../redux/reducer.js";
import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import GenericVariantsView from "./GenericVariantsView.js";
import { renderVariantAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";

interface IVariantsGenericVariantsViewOwnProps {
  partialVariantsTableSpec: InputTableQuerySpec; // without id or refGenomeId
  tableTitle: string;
}

const VariantsGenericVariantsView: VFC<IVariantsGenericVariantsViewOwnProps> = (props) => {
  const { variantId } = useParams<"variantId">();
  const { mutation } = useSelector<IAppState, IMutationState>((state) => state.mutation);
  const refGenomeId = useSelector<IAppState>((state) => state.auth.session?.params?.referenceGenome);
  const viewId = useURLViewID();

  // using property isLoadingMutation (=state.mutation.isLoading) does lead to reset of the grid when the ref genome is changed
  // therefore we use the following assertion instead (=> loading spinner when changing mutation, but not when changing ref genome)
  const mutationDataReady = Number.parseInt(mutation?.id) === toSafeInt(variantId);
  const analysisGroup: VariantAnalysisGroup = mutationDataReady && mutation.refVariant.variantAnalysisGroup;

  // embed the parent id
  const tableSpec: InputTableQuerySpec = mutationDataReady &&
    variantId && {
      ...props.partialVariantsTableSpec,
      id: variantId,
      restrictions: { refGenomeId },
    };

  return (
    <GenericVariantsView
      title={mutation?.refVariant.name}
      subtitle={mutation?.refVariant.description || props.tableTitle}
      headerAttributes={[
        <AttributesListWidget
          attributes={renderVariantAttributesWidgets(mutation?.refVariant)}
          key={"variant-genome-view-widget-1"}
        />,
      ]}
      tableSpec={tableSpec}
      variantAnalysisGroup={analysisGroup}
      viewId={viewId ?? undefined}
      getSummaryEntityName={() => mutation?.refVariant?.name}
    />
  );
};

export default VariantsGenericVariantsView;
